import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { authStorage } from "@tiny/utils";
import {
  AudienceRoutes,
  TinyCapabilities,
  TinyContactImporter,
  TinyHost,
  TinyHostProvider,
  TinyLoader,
  TinyNavigation,
  TinyNotifier,
  TinyProgressToastOptions,
  TinyRefreshBus,
  TinyUpsell,
} from "@tiny/host-contract";
import { useConfirm } from "@tiny/components";
import { useRefresh } from "@tiny/components/src/RefreshProvider";
import { enqueueSnackbar, closeSnackbar } from "notistack";
import { useScreenLoaderInvokers } from "@tiny/lib";
import { ThemeProvider } from "@mui/material/styles";
import { einsteinTheme } from "@tiny/theme/createTheme";
import { showUploadMessage } from "../../../components/Snackbar/snackbar-helper";
import ProductDetailsDialog from "./ProductDetailsDialog";

// TinyAlbert's own Clean-My-List product (distinct from email's). The shared
// audience passes email's productId to the slot; Albert's slot ignores it and
// uses these instead.
const ALBERT_CLEAN_LIST_PRODUCT_IDS = {
  STAGE: "ff2d8ca0-8019-4a80-b317-3428ab04553b",
  PROD: "6df2e7bd-7687-4221-ac62-acf85f45e39b",
};

/**
 * TinyAlbert's implementation of the @tiny/host-contract TinyHost. Unlike email,
 * Albert unlocks every audience feature (no paywall — `isFeatureEnabled` always
 * true) and hides the three email-only surfaces via the host toggles:
 * suppression list, audience (AI) summary, and Retention.com.
 *
 * Router glue uses React Router v6 (useNavigate/useLocation); the shared audience
 * pages stay router-free and drive all navigation through `navigate.*`.
 */

// Audience URLs are identical to email — the feature lives at /main/audience.
const audienceRoutes: AudienceRoutes = {
  home: (tab?: string) =>
    tab ? `/main/audience?tab=${tab}` : "/main/audience",
  sourcesAll: () => "/main/audience/sources/all",
  sourceDetails: (id: string) => `/main/audience/sources/${id}`,
  suppressionDetails: (id: string) => `/main/audience/suppression/${id}`,
  editSegment: (id: string) => `/main/audience/edit-segments/${id}`,
  customSegments: () => "/main/audience?tab=custom_segments",
  integrations: () => "/main/integrations",
};

interface AlbertTokenClaims {
  accountId?: string;
  sub?: string;
  accountType?: string;
  campaignWorkflowType?: string;
  [key: string]: unknown;
}

function decodeToken(): AlbertTokenClaims | null {
  try {
    const token = authStorage.getAccessToken();
    if (!token) return null;
    return JSON.parse(atob(token.split(".")[1]));
  } catch {
    return null;
  }
}

function AlbertHostProvider({ children }: { children: React.ReactNode }) {
  const navigateFn = useNavigate();
  const location = useLocation();
  const locationRef = React.useRef(location);
  locationRef.current = location;

  const confirm = useConfirm();
  const refreshCtx = useRefresh();

  const token = React.useMemo(decodeToken, []);

  const navigate = React.useMemo<TinyNavigation>(
    () => ({
      to: (path, options) => navigateFn(path, { state: options?.state }),
      replace: (path, options) =>
        navigateFn(path, { replace: true, state: options?.state }),
      getLocationState: () => locationRef.current.state,
      getReturnPath: () => {
        const state = locationRef.current.state as {
          backUrl?: string | { pathname?: string; search?: string };
        } | null;
        const backUrl = state?.backUrl;
        if (typeof backUrl === "string") return backUrl;
        if (backUrl?.pathname)
          return `${backUrl.pathname}${backUrl.search ?? ""}`;
        return undefined;
      },
      getSearch: () => locationRef.current.search,
      getPathname: () => locationRef.current.pathname,
      routes: { audience: audienceRoutes },
    }),
    // Depend on `location` so the host object gets a new identity on every
    // navigation. That re-renders useTinyHost consumers (e.g. useStringParam
    // reading ?tab=) — otherwise a query-only change wouldn't switch tabs.
    [navigateFn, location]
  );

  // Route through einstein's own notistack toasts (its root provider renders
  // them). @tiny/components MessagesProvider only stores messages — it has no
  // UI here — so notifications never showed.
  const notify = React.useMemo<TinyNotifier>(() => {
    const toast = (
      message: React.ReactNode,
      variant: "error" | "warning" | "info" | "success"
    ) => String(enqueueSnackbar(message, { variant }));
    const uploadVariant = (
      v: string
    ): "uploading" | "uploadSuccess" | "uploadFailed" | "merging" => {
      if (v === "failed") return "uploadFailed";
      if (v === "success") return "uploadSuccess";
      if (v === "merging") return "merging";
      return "uploading";
    };
    const progress = (o: TinyProgressToastOptions) =>
      showUploadMessage({
        variant: uploadVariant(o.variant),
        message: o.message,
        description: o.description,
        persist: o.persist,
        ctaActionText: o.primaryCtaActionText,
        ctaActionFunction: o.primaryCtaActionFunction,
        secondaryCtaActionText: o.secondaryCtaActionText,
        secondaryCtaActionFunction: o.secondaryCtaActionFunction,
        showClose: o.showClose,
      });
    return {
      showError: (message) => toast(message, "error"),
      showWarning: (message) => toast(message, "warning"),
      showInfo: (message) => toast(message, "info"),
      showSuccess: (message) => toast(message, "success"),
      close: (id) => closeSnackbar(id),
      showUpload: progress,
      showMerge: progress,
    };
  }, []);

  const refresh = React.useMemo<TinyRefreshBus>(
    () => ({
      refresh: refreshCtx.refresh,
      registerCallback: refreshCtx.registerCallback,
      refreshing: refreshCtx.refreshing,
    }),
    [refreshCtx]
  );

  // Albert doesn't wire a Flatfile importer through the host; the audience home's
  // own import dialog handles CSV uploads. These stubs satisfy the contract.
  const importer = React.useMemo<TinyContactImporter>(
    () => ({
      openImporter: () => undefined,
      closeImporter: () => undefined,
      openImportDialog: () => undefined,
      closeImportDialog: () => undefined,
    }),
    []
  );

  // No paywall in Albert, so upsell is never triggered; no-op keeps the contract.
  const upsell = React.useMemo<TinyUpsell>(
    () => ({
      showFeatureUpgrade: () => undefined,
      showPlanSelection: () => undefined,
    }),
    []
  );

  // Wire the shared feature's blocking loader to einstein's screen loader.
  const { showScreenLoader, hideScreenLoader } = useScreenLoaderInvokers();
  const loader = React.useMemo<TinyLoader>(
    () => ({ show: showScreenLoader, hide: hideScreenLoader }),
    [showScreenLoader, hideScreenLoader]
  );

  const capabilities = React.useMemo<TinyCapabilities>(
    () => ({
      accountId: token?.accountId,
      userId: token?.sub,
      accountType: token?.accountType,
      campaignWorkflowType: token?.campaignWorkflowType,
      aiTrafficControlEnabled: false,
      // Treat Albert as the top plan so plan-code-gated features (e.g. the
      // Segment Library) are unlocked — Albert has no audience paywall.
      planCode: "pro",
      isShopifyPlan: false,
      isAppSumoPlan: false,
      isGroovePlan: false,
      featuresLoading: false,
      isStagingEnvironment: (import.meta.env.VITE_APP_API_URL || "").includes(
        "stage"
      ),
      globalActiveEngagerVisible: false,
      actionsDisabled: false,
      // Albert-only: hide the email-specific surfaces.
      suppressionListVisible: false,
      audienceSummaryVisible: false,
      retentionVisible: false,
      newEngagerPerformanceVisible: false,
      cleanListVisible: true,
      integrationsVisible: false,
      // Embedded in Shopify — keep navigation inside the iframe.
      subscriberHistorySameTab: true,
      // Every feature unlocked — no paywall.
      isFeatureEnabled: () => true,
      featureDetails: () => ({ enabled: true }),
    }),
    [token]
  );

  // Clean-My-List: the shared audience renders this app-provided dialog. Albert
  // reuses its own ProductDetailsDialog with TinyAlbert's product id.
  const slots = React.useMemo(
    () => ({
      /* eslint-disable react/no-unused-prop-types */
      productDetailsDialog: (props: {
        open: boolean;
        onClose: () => void;
        productId: string;
        segmentId: string;
        onSuccess: () => void;
      }) => {
        const isStaging = (import.meta.env.VITE_APP_API_URL || "").includes(
          "stage"
        );
        return (
          // Render under einstein's own theme — this dialog's @mui/material
          // typography/scale was built for it, not the @tiny/components audience
          // theme (which would oversize the headings).
          <ThemeProvider theme={einsteinTheme}>
            <ProductDetailsDialog
              open={props.open}
              onClose={props.onClose}
              productId={
                isStaging
                  ? ALBERT_CLEAN_LIST_PRODUCT_IDS.STAGE
                  : ALBERT_CLEAN_LIST_PRODUCT_IDS.PROD
              }
              segmentId={props.segmentId}
            />
          </ThemeProvider>
        );
      },
    }),
    []
  );

  const host = React.useMemo<TinyHost>(
    () => ({
      appName: "einstein",
      navigate,
      notify,
      confirm: confirm as unknown as TinyHost["confirm"],
      refresh,
      importer,
      capabilities,
      upsell,
      loader,
      slots,
    }),
    [
      navigate,
      notify,
      confirm,
      refresh,
      importer,
      capabilities,
      upsell,
      loader,
      slots,
    ]
  );

  return <TinyHostProvider host={host}>{children}</TinyHostProvider>;
}

export default AlbertHostProvider;
